import { useLayoutEffect, useRef, useState } from 'react';
import './ScannerViewport.css';
import { ROI_CROP_FILL } from '../utils/runInspection';
import {
  centerRoiToContainerPercent,
  getCoverMapping,
  normToContainerPercent,
} from '../utils/overlayMap';

export default function ScannerViewport({
  videoRef,
  distanceState,
  targetDistanceCm = 15,
  toleranceCm = 3,
  starting = false,
  cameraReady = false,
  cameraError = '',
  needsTap = false,
  overlaySpots = [],
  torchOn = false,
  torchSupported = false,
  onToggleTorch,
  onStartCamera,
}) {
  const status = distanceState?.status || 'unknown';
  const direction = distanceState?.direction || 'none';
  const canScan = Boolean(distanceState?.canScan);
  const rootRef = useRef(null);
  const [guideStyle, setGuideStyle] = useState({
    left: '11%',
    top: '11%',
    width: '78%',
    height: '78%',
  });
  const [mappedSpots, setMappedSpots] = useState([]);

  useLayoutEffect(() => {
    const video = videoRef?.current;
    const root = rootRef.current;
    if (!video || !root) return undefined;

    const update = () => {
      const mapping = getCoverMapping(video, root);
      const guide = centerRoiToContainerPercent(ROI_CROP_FILL, mapping);
      if (guide) {
        setGuideStyle({
          left: `${guide.left}%`,
          top: `${guide.top}%`,
          width: `${guide.width}%`,
          height: `${guide.height}%`,
        });
      }

      if (!mapping || !overlaySpots?.length) {
        setMappedSpots([]);
        return;
      }

      setMappedSpots(
        overlaySpots
          .map((spot) => {
            const box = normToContainerPercent(
              { nx: spot.nx, ny: spot.ny, nw: spot.nw, nh: spot.nh },
              mapping
            );
            if (!box) return null;
            return { ...spot, ...box };
          })
          .filter(Boolean)
      );
    };

    update();
    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(update) : null;
    if (ro) ro.observe(root);
    video.addEventListener('loadedmetadata', update);
    const timer = setInterval(update, 500);

    return () => {
      if (ro) ro.disconnect();
      video.removeEventListener('loadedmetadata', update);
      clearInterval(timer);
    };
  }, [videoRef, overlaySpots, cameraReady]);

  return (
    <div ref={rootRef} className={`scanner-viewport status-${status}`}>
      <video
        ref={videoRef}
        playsInline
        muted
        autoPlay
        className="scanner-video"
      />

      {!cameraReady && (
        <div className="scanner-status-overlay">
          {starting ? (
            <>
              <strong>กำลังเปิดกล้อง...</strong>
              <span>กดอนุญาตถ้า Safari ถาม</span>
            </>
          ) : needsTap ? (
            <>
              <strong>Safari ต้องกดเปิดกล้องเอง</strong>
              <span>กดปุ่มด้านล่างเพื่อเริ่ม live camera</span>
              <button type="button" className="btn btn-primary overlay-start-btn" onClick={onStartCamera}>
                เปิดกล้อง
              </button>
            </>
          ) : (
            <>
              <strong>กล้องยังไม่เปิด</strong>
              <span>{cameraError || 'กดปุ่ม「เปิดกล้อง」ด้านล่าง'}</span>
              <button type="button" className="btn btn-primary overlay-start-btn" onClick={onStartCamera}>
                เปิดกล้อง
              </button>
            </>
          )}
        </div>
      )}

      {cameraReady && direction === 'away' && (
        <div className="distance-arrow away" aria-hidden>
          <div className="arrow-glyph">↑</div>
          <div className="arrow-text">ถอยออก</div>
        </div>
      )}
      {cameraReady && direction === 'closer' && (
        <div className="distance-arrow closer" aria-hidden>
          <div className="arrow-glyph">↓</div>
          <div className="arrow-text">เข้าใกล้</div>
        </div>
      )}

      <div className="scanner-guide" style={guideStyle}>
        {/* guide frame only — spots drawn on full viewport for correct cover mapping */}
      </div>

      <div className="spot-layer">
        {mappedSpots.map((spot) => {
          const cx = spot.left + spot.width / 2;
          const cy = spot.top + spot.height / 2;
          const pad = Math.max(
            Math.max(spot.width, spot.height) * 0.9,
            2.4
          );
          const boxSide = Math.min(
            42,
            Math.max(Math.max(spot.width, spot.height) + pad * 2, 7)
          );
          let left = cx - boxSide / 2;
          let top = cy - boxSide / 2;
          if (left < 0) left = 0;
          if (top < 0) top = 0;
          if (left + boxSide > 100) left = Math.max(0, 100 - boxSide);
          if (top + boxSide > 100) top = Math.max(0, 100 - boxSide);

          const sizeClass = spot.oversized ? 'ng' : 'ok';
          return (
            <div
              key={spot.id}
              className={`spot-box ${sizeClass}`}
              style={{
                left: `${left}%`,
                top: `${top}%`,
                width: `${boxSide}%`,
                height: `${boxSide}%`,
              }}
              title={spot.label}
            >
              <span className="spot-corner tl" />
              <span className="spot-corner tr" />
              <span className="spot-corner bl" />
              <span className="spot-corner br" />
              <span className={`spot-label ${sizeClass}`}>
                {spot.isKnot ? 'ตาไม้ ' : 'จุดด่างดำ '}Ø{Number(spot.diameterMm).toFixed(1)}
                {spot.oversized ? ' >5' : ''}
              </span>
            </div>
          );
        })}
      </div>

      <div className="scanner-topbar">
        <button
          type="button"
          className={`torch-btn ${torchOn ? 'on' : 'off'}`}
          disabled={!torchSupported || !cameraReady}
          onClick={onToggleTorch}
        >
          {torchOn ? 'ไฟเปิดอยู่' : 'เปิดไฟ'}
        </button>
        <div className="topbar-right">
          {cameraReady && canScan && (
            <span className="hud-pill ready">✓ ระยะ OK</span>
          )}
          {!canScan ? (
            <span className="hud-pill ng">ห้ามอ่าน · รอระยะ</span>
          ) : overlaySpots.length > 0 ? (
            <span className="hud-pill warn">พบ {overlaySpots.length} จุด</span>
          ) : cameraReady ? (
            <span className="hud-pill unknown">กำลังตรวจ...</span>
          ) : null}
        </div>
      </div>

      <div className="scanner-hud">
        <span className={`hud-pill ${status}`}>
          {distanceState?.arrow ? `${distanceState.arrow} ` : ''}
          {distanceState?.label}
        </span>
        <span className="hud-hint">
          {distanceState?.hint ||
            `เป้า ${targetDistanceCm} ซม. (±${toleranceCm} ซม.)`}
        </span>
      </div>
    </div>
  );
}
